import { type ReactNode, useRef, useEffect, useContext } from 'react';
import { createStyles, Stack, Flex, Divider } from '@mantine/core';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import useTranslation from 'next-translate/useTranslation';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';

import type { UserEntrant } from '~/domains/contest/domains/services/interfaces/entrant.interface';
import { Text } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';
import { Avatar } from '~/domains/design-system/Avatar';
import { UserContext } from '~/components/providers/UserProvider';
import EntrantsSkeletons from '~/domains/contest/domains/common/components/Entrants/EntrantsSkeletons';
import UserExperienceLevelBadge from '~/domains/contest/domains/common/components/Entrants/UserExperienceLevelBadge/UserExperienceLevelBadge';
import { FullScreenMessage } from '~/domains/common/components/FullScreenMessage/FullScreenMessage';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import { useEntrantFollow } from '~/domains/contest/domains/detail/components/Entrants/useEntrantFollow';
import type { PaginatedData } from '~/domains/services/interfaces/paginated-data.interface';

type EntrantsListProps = {
  items?: UserEntrant[];
  isLoading: boolean;
  isError: boolean;
  source?: 'contest_detail_entrants' | 'upcoming_entrants';
  contestId: string;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  refetch: () => void;
};

const useStyles = createStyles((theme) => ({
  container: {
    width: '100%',
  },
  listWrapper: {
    position: 'relative',
    width: '100%',
  },
  itemWrapper: {
    width: '100%',
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ '8px 0',
  },
  entrantItem: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
    padding: `${theme.other.spacing._8}px ${theme.other.spacing._16}px`,
    cursor: 'pointer',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    borderRadius: theme.radius.sm,
    padding: theme.other.spacing._4,
  },
  nameGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  relationshipText: {
    color: theme.colors.gray[6],
  },
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

function EntrantsList({
  items = [],
  isLoading,
  isError,
  source = 'contest_detail_entrants',
  contestId,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  refetch,
}: EntrantsListProps) {
  const { t } = useTranslation('contest');
  const { classes, theme } = useStyles();
  const { openModal } = useContext(RootModalsContext);
  const queryClient = useQueryClient();

  const { user } = useContext(UserContext);

  const { follow } = useEntrantFollow({ source, contestId });

  const listRef = useRef<HTMLDivElement>(null);

  const virtualizer = useWindowVirtualizer({
    count: hasNextPage ? items.length + 1 : items.length,
    estimateSize: () => 60,
    overscan: 5,
    scrollMargin: listRef.current?.offsetTop ?? 0,
    gap: /* eslint-disable-line local-rules/use-theme-tokens */ 1,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const [lastItem] = virtualItems.slice(-1);

    if (!lastItem) return;

    if (lastItem.index >= items.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, items.length, virtualItems]);

  const handleEntrantClick = async (entrant: UserEntrant) => {
    openModal({
      type: 'EntrantDetailModal',
      entrant,
      source,
      contestId,
    });
  };

  if (isLoading && items.length === 0) {
    return <EntrantsSkeletons count={20} />;
  }

  if (isError) {
    return (
      <Flex h="50svh" justify="center" align="center">
        <FullScreenMessage
          icon={<IconAlertTriangle color={theme.colors.orange[3]} size={24} />}
          title={t('common.entrants.list.error')}
          subtitle={t('common.entrants.list.errorSubtitle')}
          actions={
            <Button variant="secondary-fill" size="medium" onClick={refetch}>
              {t('common.entrants.list.retry')}
            </Button>
          }
        />
      </Flex>
    );
  }

  if (items.length === 0) {
    return (
      <Flex h="50svh" justify="center" align="center">
        <FullScreenMessage
          icon={<IconInfoCircle color={theme.colors.blue[3]} size={24} />}
          title={t('common.entrants.list.noEntrants')}
        />
      </Flex>
    );
  }

  const getRelationshipStatus = (entrant: UserEntrant): ReactNode => {
    const cachedData = queryClient.getQueryData<{ pages: PaginatedData<UserEntrant>[] }>([
      'contestEntrantsInfinite',
      contestId,
      source,
    ]);
    const cachedEntrant = cachedData?.pages
      ?.flatMap((page) => page.data)
      ?.find((e) => e.id === entrant.id);

    const isFollowing = cachedEntrant?.isFollowing || entrant.isFollowing;

    if (isFollowing && entrant.isFollowed) {
      return (
        <Text variant="body-small" className={classes.relationshipText}>
          {t('common.entrants.list.mutuallyFollowing')}
        </Text>
      );
    }

    if (isFollowing) {
      return (
        <Text variant="body-small" className={classes.relationshipText}>
          {t('common.entrants.list.following')}
        </Text>
      );
    }

    if (entrant.isFollowed) {
      return (
        <Text variant="body-small" className={classes.relationshipText}>
          {t('common.entrants.list.followsYou')}
        </Text>
      );
    }

    return null;
  };

  const shouldShowFollowButton = (entrant: UserEntrant) => {
    if (!entrant?.id || entrant?.id === user?.id) {
      return false;
    }

    const cachedData = queryClient.getQueryData<{ pages: PaginatedData<UserEntrant>[] }>([
      'contestEntrantsInfinite',
      contestId,
      source,
    ]);
    const cachedEntrant = cachedData?.pages
      ?.flatMap((page) => page.data)
      ?.find((e) => e.id === entrant.id);

    const isFollowing = cachedEntrant?.isFollowing || entrant.isFollowing;

    return !isFollowing;
  };

  return (
    <div className={classes.container}>
      <div
        className={classes.listWrapper}
        ref={listRef}
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualRow) => {
          const isLoader = virtualRow.index === items.length;
          const item = items[virtualRow.index];

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
              }}
            >
              <div
                className={classes.entrantItem}
                onClick={() => handleEntrantClick(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleEntrantClick(item);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {isLoader ? (
                  <EntrantsSkeletons count={5} />
                ) : (
                  <>
                    <div className={classes.userInfo}>
                      <Avatar radius="xl" size={40} src={item.avatarUrl}>
                        {item.handle?.[0]}
                      </Avatar>
                      <Stack spacing={theme.other.spacing._2}>
                        <div className={classes.nameGroup}>
                          <Text variant="subhead-small">
                            {item.handle}
                            {item.entriesCount > 1 ? ` (${item.entriesCount})` : ''}
                          </Text>
                          <UserExperienceLevelBadge level={item.experienceLevel} />
                        </div>
                        {getRelationshipStatus(item)}
                      </Stack>
                    </div>
                    <div>
                      {shouldShowFollowButton(item) ? (
                        <Button
                          variant="secondary-fill"
                          size="small"
                          onClick={(e) => follow.handleFollow(item.id, item.channelId, e)}
                          disabled={follow.isLoading}
                        >
                          {t('common.entrants.list.action')}
                        </Button>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
              <Divider color={theme.colors.gray2[0]} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default EntrantsList;
